export const HEATMAP_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

export const MONTH_ORDER = [...HEATMAP_MONTHS];

export const toMonthShort = (monthName = "") => {
  const month = monthName.toString().trim().toLowerCase();
  if (!month) return "";

  const map = {
    january: "Jan",
    february: "Feb",
    march: "Mar",
    april: "Apr",
    may: "May",
    june: "Jun",
    july: "Jul",
    august: "Aug",
    september: "Sep",
    october: "Oct",
    november: "Nov",
    december: "Dec",
    jan: "Jan",
    feb: "Feb",
    mar: "Mar",
    apr: "Apr",
    jun: "Jun",
    jul: "Jul",
    aug: "Aug",
    sep: "Sep",
    oct: "Oct",
    nov: "Nov",
    dec: "Dec",
  };

  return map[month] || month.slice(0, 1).toUpperCase() + month.slice(1, 3);
};

export const toNumericValue = (value) => {
  if (typeof value === "number") return value;
  if (typeof value !== "string") return 0;

  const cleaned = value.replace(/[^0-9.-]/g, "");
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const clampPercent = (value) => {
  const numericValue = toNumericValue(value);
  if (numericValue < 0) return 0;
  if (numericValue > 100) return 100;
  return numericValue;
};

export const parseDailyRevenueData = (payload) => {
  if (!Array.isArray(payload)) {
    return { categories: [], values: [] };
  }

  const rows = payload
    .map((item, index) => {
      if (!item || typeof item !== "object") return null;

      const dateValue =
        item.Date ||
        item.date ||
        item.Day ||
        item.day ||
        item.RevenueDate ||
        item.revenueDate ||
        item.BookingDate ||
        item.bookingDate ||
        null;

      const revenueValue =
        item.Revenue ??
        item.revenue ??
        item.TotalRevenue ??
        item.totalRevenue ??
        item.DailyRevenue ??
        item.dailyRevenue ??
        item.Amount ??
        item.amount ??
        0;

      const revenue = toNumericValue(revenueValue);
      const parsedDate = dateValue ? new Date(dateValue) : null;
      const sortableDate =
        parsedDate && !Number.isNaN(parsedDate.getTime())
          ? parsedDate.getTime()
          : index;

      const label =
        parsedDate && !Number.isNaN(parsedDate.getTime())
          ? parsedDate.toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
            })
          : String(dateValue || `Day ${index + 1}`);

      return {
        label,
        revenue,
        sortKey: sortableDate,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.sortKey - b.sortKey);

  return {
    categories: rows.map((row) => row.label),
    values: rows.map((row) => row.revenue),
  };
};

export const REVENUE_FILTER_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
];

const REVENUE_VALUE_KEYS = [
  "Revenue",
  "revenue",
  "TotalRevenue",
  "totalRevenue",
  "DailyRevenue",
  "dailyRevenue",
  "MonthlyRevenue",
  "monthlyRevenue",
  "YearlyRevenue",
  "yearlyRevenue",
  "Amount",
  "amount",
  "TotalAmount",
  "totalAmount",
  "Value",
  "value",
  "Total",
  "total",
];

const REVENUE_META_KEYS = new Set([
  "currentMonthRevenue",
  "lastMonthRevenue",
  "currentMonth",
  "lastMonth",
]);

const resolveMonthlyLabelFromKey = (key = "") => {
  const normalizedKey = key.toString().trim().toLowerCase();
  if (!normalizedKey) return "";

  const numericMonth = Number(normalizedKey);
  if (Number.isFinite(numericMonth) && numericMonth >= 1 && numericMonth <= 12) {
    return HEATMAP_MONTHS[numericMonth - 1];
  }

  const monthShort = toMonthShort(normalizedKey);
  if (HEATMAP_MONTHS.includes(monthShort)) return monthShort;

  const compact = normalizedKey.replace(/[\s_-]/g, "");
  const monthMatch = compact.match(/^(month|m)(\d{1,2})$/i);
  if (monthMatch) {
    const monthIndex = Number(monthMatch[2]) - 1;
    if (monthIndex >= 0 && monthIndex < HEATMAP_MONTHS.length) {
      return HEATMAP_MONTHS[monthIndex];
    }
  }

  return "";
};

const prettifyKeyLabel = (key = "", fallback = "Point") => {
  const text = key.toString().trim();
  if (!text) return fallback;
  return text
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .replace(/^./, (char) => char.toUpperCase());
};

const toRevenueValue = (item = {}) => {
  if (!item || typeof item !== "object") return 0;

  const matchingKey = REVENUE_VALUE_KEYS.find((key) => key in item);
  if (matchingKey) {
    return toNumericValue(item[matchingKey]);
  }

  return 0;
};

export const getRevenueValue = (payload) => {
  if (payload == null) return 0;
  if (typeof payload === "number" || typeof payload === "string") {
    return toNumericValue(payload);
  }

  if (Array.isArray(payload)) {
    return payload.reduce((sum, item) => sum + getRevenueValue(item), 0);
  }

  if (typeof payload === "object") {
    const directValue = toRevenueValue(payload);
    if (directValue) return directValue;

    const nestedKeys = [
      "data",
      "item",
      "result",
      "summary",
      "currentMonthRevenue",
      "lastMonthRevenue",
    ];

    for (const key of nestedKeys) {
      if (key in payload) {
        const nestedValue = getRevenueValue(payload[key]);
        if (nestedValue) return nestedValue;
      }
    }
  }

  return 0;
};

const getLabelFromItem = (item = {}, type = "trend", index = 0) => {
  if (!item || typeof item !== "object") {
    return { label: `Point ${index + 1}`, sortKey: index };
  }

  if (type === "yearly") {
    const yearValue = item.Year || item.year || item.Label || item.label || item.x;
    const parsedYear = Number(yearValue);
    return {
      label: String(yearValue || `Year ${index + 1}`),
      sortKey: Number.isFinite(parsedYear) ? parsedYear : index,
    };
  }

  if (type === "monthly") {
    const monthValue =
      item.Month ||
      item.month ||
      item.MonthNo ||
      item.monthNo ||
      item.MonthNumber ||
      item.monthNumber ||
      item.MonthId ||
      item.monthId ||
      item.MonthName ||
      item.monthName ||
      item.Label ||
      item.label ||
      item.x;
    const monthShort = resolveMonthlyLabelFromKey(monthValue) || toMonthShort(monthValue);
    const monthIndex = HEATMAP_MONTHS.findIndex((month) => month === monthShort);
    return {
      label: monthShort || String(monthValue || `Month ${index + 1}`),
      sortKey: monthIndex >= 0 ? monthIndex : index,
    };
  }

  const dateValue =
    item.Date ||
    item.date ||
    item.Day ||
    item.day ||
    item.RevenueDate ||
    item.revenueDate ||
    item.BookingDate ||
    item.bookingDate ||
    item.Label ||
    item.label ||
    item.x;

  const parsedDate = dateValue ? new Date(dateValue) : null;
  if (parsedDate && !Number.isNaN(parsedDate.getTime())) {
    return {
      label: parsedDate.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      }),
      sortKey: parsedDate.getTime(),
    };
  }

  return {
    label: String(dateValue || `Point ${index + 1}`),
    sortKey: index,
  };
};

const extractRevenueRows = (payload, type = "trend") => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];

  const preferredKeys = {
    daily: ["daily", "dailyRevenue", "dailyData"],
    monthly: ["monthly", "monthlyRevenue", "monthlyData"],
    yearly: ["yearly", "yearlyRevenue", "yearlyData"],
    trend: ["trend", "revenueTrend", "trendData"],
  };

  const fallbackKeys = ["rows", "list", "items", "values", "series", "data"];

  const keysToCheck = [
    ...(preferredKeys[type] || []),
    ...fallbackKeys,
  ];

  for (const key of keysToCheck) {
    if (Array.isArray(payload[key])) {
      return payload[key];
    }
  }

  const objectRows = Object.entries(payload)
    .filter(([key, value]) => {
      if (REVENUE_META_KEYS.has(key)) return false;
      if (value == null) return false;
      if (Array.isArray(value)) return false;
      if (typeof value === "object") return false;
      return true;
    })
    .map(([key, value], index) => {
      if (type === "monthly") {
        const monthLabel = resolveMonthlyLabelFromKey(key);
        const monthIndex = HEATMAP_MONTHS.findIndex((month) => month === monthLabel);
        return {
          Label: monthLabel || prettifyKeyLabel(key, `Month ${index + 1}`),
          Revenue: toNumericValue(value),
          __sortKey: monthIndex >= 0 ? monthIndex : index,
        };
      }

      if (type === "yearly") {
        const parsedYear = Number(key);
        return {
          Label: /^\d{4}$/.test(key) ? key : prettifyKeyLabel(key, `Year ${index + 1}`),
          Revenue: toNumericValue(value),
          __sortKey: Number.isFinite(parsedYear) ? parsedYear : index,
        };
      }

      const parsedDate = new Date(key);
      if (!Number.isNaN(parsedDate.getTime())) {
        return {
          Label: parsedDate.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
          }),
          Revenue: toNumericValue(value),
          __sortKey: parsedDate.getTime(),
        };
      }

      return {
        Label: prettifyKeyLabel(key, `Point ${index + 1}`),
        Revenue: toNumericValue(value),
        __sortKey: index,
      };
    });

  if (objectRows.length) {
    return objectRows;
  }

  return [];
};

const getCategoryHints = (payload = {}, type = "trend") => {
  if (!payload || typeof payload !== "object") return [];

  const keysByType = {
    daily: ["categories", "labels", "days", "dates", "xaxis"],
    monthly: ["categories", "labels", "months", "monthLabels", "xaxis"],
    yearly: ["categories", "labels", "years", "yearLabels", "xaxis"],
    trend: ["categories", "labels", "timeline", "xaxis"],
  };

  const fallbackKeys = ["categories", "labels", "xaxis"];
  const keysToCheck = [...(keysByType[type] || []), ...fallbackKeys];

  for (const key of keysToCheck) {
    if (Array.isArray(payload[key])) {
      return payload[key].map((item, index) => {
        const raw = String(item ?? "").trim();
        if (!raw) return "";

        if (type === "monthly") {
          return resolveMonthlyLabelFromKey(raw) || toMonthShort(raw) || `Month ${index + 1}`;
        }

        if (type === "yearly") {
          const year = Number(raw);
          if (Number.isFinite(year) && `${year}`.length === 4) return String(year);
          return prettifyKeyLabel(raw, `Year ${index + 1}`);
        }

        if (type === "daily" || type === "trend") {
          const parsedDate = new Date(raw);
          if (!Number.isNaN(parsedDate.getTime())) {
            return parsedDate.toLocaleDateString("en-IN", {
              day: "2-digit",
              month: "short",
            });
          }
        }

        return prettifyKeyLabel(raw, `Point ${index + 1}`);
      });
    }
  }

  return [];
};

const isPlaceholderLabel = (label = "") => {
  const text = label.toString().trim().toLowerCase();
  return (
    /^point\s+\d+$/.test(text) ||
    /^month\s+\d+$/.test(text) ||
    /^day\s+\d+$/.test(text) ||
    /^year\s+\d+$/.test(text)
  );
};

const getDefaultLabelByType = (type = "trend", index = 0) => {
  if (type === "monthly") {
    return HEATMAP_MONTHS[index] || `Month ${index + 1}`;
  }
  if (type === "yearly") {
    return `Year ${index + 1}`;
  }
  if (type === "daily") {
    return `Day ${index + 1}`;
  }
  return `Point ${index + 1}`;
};

export const parseRevenueApiData = (payload, type = "trend") => {
  const rawRows = extractRevenueRows(payload, type);
  const categoryHints = getCategoryHints(payload, type);
  const rows = rawRows.map((item, index) => {
    if (item && typeof item === "object") return item;
    return {
      Label: categoryHints[index] || getDefaultLabelByType(type, index),
      Revenue: item,
      __sortKey: index,
    };
  });

  const points = rows
    .map((item, index) => {
      const { label, sortKey } = getLabelFromItem(item, type, index);
      const candidateSortKeys = [item?.__sortKey, item?.SortKey, item?.sortKey];
      const overrideSortKeyRaw = candidateSortKeys.find((value) =>
        Number.isFinite(Number(value)),
      );
      const overrideSortKey = Number(overrideSortKeyRaw);
      return {
        label:
          categoryHints[index] ||
          (isPlaceholderLabel(label) ? getDefaultLabelByType(type, index) : label),
        value: toRevenueValue(item),
        sortKey: Number.isFinite(overrideSortKey) ? overrideSortKey : sortKey,
      };
    })
    .sort((a, b) => a.sortKey - b.sortKey);

  const currentFromPayload = toNumericValue(payload?.currentMonthRevenue);
  const previousFromPayload = toNumericValue(payload?.lastMonthRevenue);
  const hasMonthlyKpi =
    payload &&
    typeof payload === "object" &&
    ("currentMonthRevenue" in payload || "lastMonthRevenue" in payload);

  const currentValue = hasMonthlyKpi
    ? currentFromPayload
    : points.length
      ? points[points.length - 1].value
      : 0;
  const previousValue = hasMonthlyKpi
    ? previousFromPayload
    : points.length > 1
      ? points[points.length - 2].value
      : 0;

  const delta = currentValue - previousValue;
  const deltaPercent = previousValue > 0 ? (delta / previousValue) * 100 : 0;

  if (!points.length && type === "monthly" && hasMonthlyKpi) {
    return {
      categories: ["Last Month", "Current Month"],
      values: [previousFromPayload, currentFromPayload],
      insight: {
        currentValue,
        previousValue,
        delta,
        deltaPercent,
      },
    };
  }

  return {
    categories: points.map((point) => point.label),
    values: points.map((point) => point.value),
    insight: {
      currentValue,
      previousValue,
      delta,
      deltaPercent,
    },
  };
};

export const parseOccupancyData = (payload) => {
  const buildEntriesFromTotal = (totalRow = {}) => {
    const totalRooms = Number(totalRow.TotalRooms) || 0;
    const occupiedRooms = Number(totalRow.OccupiedRooms) || 0;
    const availableRooms = Number(totalRow.AvailableRooms) || 0;
    const otherRooms = Math.max(totalRooms - (occupiedRooms + availableRooms), 0);

    const entries = [
      { label: "Occupied Rooms", value: occupiedRooms },
      { label: "Available Rooms", value: availableRooms },
    ];

    if (otherRooms > 0) {
      entries.push({ label: "Management Block Rooms", value: otherRooms });
    }

    return {
      entries: entries.filter((item) => item.value >= 0),
      summary: {
        totalRooms,
        occupiedRooms,
        availableRooms,
        bookingPercentage: Number(totalRow.BookingPercentage) || 0,
      },
    };
  };

  if (Array.isArray(payload)) {
    const totalRow = payload.find(
      (item) =>
        item &&
        typeof item === "object" &&
        (item?.RoomConfig?.toString().trim().toUpperCase() === "TOTAL" ||
          (item?.PropertyID == null && item?.RoomConfigID == null)),
    );

    if (totalRow) {
      return buildEntriesFromTotal(totalRow);
    }

    const aggregate = payload.reduce(
      (acc, item) => {
        if (!item || typeof item !== "object") return acc;
        acc.totalRooms += Number(item.TotalRooms) || 0;
        acc.occupiedRooms += Number(item.OccupiedRooms) || 0;
        acc.availableRooms += Number(item.AvailableRooms) || 0;
        return acc;
      },
      {
        totalRooms: 0,
        occupiedRooms: 0,
        availableRooms: 0,
        BookingPercentage: 0,
      },
    );

    return buildEntriesFromTotal(aggregate);
  }

  if (payload && typeof payload === "object") {
    return buildEntriesFromTotal(payload);
  }

  return {
    entries: [],
    summary: {
      totalRooms: 0,
      occupiedRooms: 0,
      availableRooms: 0,
      bookingPercentage: 0,
    },
  };
};

const normalizeMonth = (monthName = "") => {
  const month = monthName.toString().trim().toLowerCase();
  if (!month) return "";
  return month.slice(0, 3);
};

export const mapMonthlyData = (response = []) => {
  const values = new Map();

  response.forEach((item) => {
    const monthKey = normalizeMonth(item.MonthName);
    if (monthKey) {
      values.set(monthKey, Number(item.TotalCount) || 0);
    }
  });

  return MONTH_ORDER.map((month) => values.get(month.toLowerCase()) || 0);
};
